import { useCallback, useMemo, useState } from "react"
import {
  CHART_HEIGHT_STORAGE_KEY,
  DEFAULT_CHART_HEIGHT,
  MAX_CHART_HEIGHT,
  MIN_CHART_HEIGHT,
} from "../constants/settings"

// Per-user chart height preference, one entry per chart key, persisted in
// localStorage (the same place the page keeps its other display prefs — table
// order and expanded/collapsed state). Keys: "explorer" for the Metrics
// Explorer chart and `${reportId}:${groupId}` (vizOverrideKey in index.tsx)
// for tables-view group charts — report-scoped because group ids are only
// unique within a report.

type StoredHeights = Record<string, number>

export function clampChartHeight(height: number): number {
  if (!Number.isFinite(height)) return DEFAULT_CHART_HEIGHT
  return Math.min(
    MAX_CHART_HEIGHT,
    Math.max(MIN_CHART_HEIGHT, Math.round(height)),
  )
}

/** Lenient: corrupt JSON, a non-object, or non-numeric entries read as absent. */
function readStoredHeights(): StoredHeights {
  if (typeof window === "undefined") return {}
  try {
    const raw = window.localStorage.getItem(CHART_HEIGHT_STORAGE_KEY)
    if (!raw) return {}
    const parsed: unknown = JSON.parse(raw)
    if (
      parsed === null ||
      typeof parsed !== "object" ||
      Array.isArray(parsed)
    ) {
      return {}
    }
    const heights: StoredHeights = {}
    for (const [key, value] of Object.entries(parsed)) {
      if (typeof value === "number" && Number.isFinite(value)) {
        heights[key] = value
      }
    }
    return heights
  } catch {
    return {}
  }
}

export function readStoredChartHeight(chartKey: string): number {
  const stored = readStoredHeights()[chartKey]
  return stored === undefined ? DEFAULT_CHART_HEIGHT : clampChartHeight(stored)
}

function writeStoredChartHeight(chartKey: string, height: number) {
  if (typeof window === "undefined") return
  try {
    // Re-read at write time: several charts are mounted at once on the tables
    // view, and each must merge into the CURRENT map, not a stale snapshot.
    const next = readStoredHeights()
    next[chartKey] = height
    window.localStorage.setItem(CHART_HEIGHT_STORAGE_KEY, JSON.stringify(next))
  } catch {
    // Storage disabled/full: the in-memory height still applies this session.
  }
}

/**
 * The committed (clamped) height for `chartKey`, plus a setter that persists.
 * With no key the hook is inert (default height, no-op setter), so a chart
 * without a size key keeps its fixed legacy height.
 */
export function useChartHeight(chartKey: string | undefined) {
  // Session overrides keyed by chart key, so a mounted chart whose key changes
  // (a report switch that keeps the same group id) reads the right entry
  // instead of carrying the previous key's height.
  const [overrides, setOverrides] = useState<StoredHeights>({})

  const height = useMemo(() => {
    if (chartKey === undefined) return DEFAULT_CHART_HEIGHT
    return overrides[chartKey] ?? readStoredChartHeight(chartKey)
  }, [overrides, chartKey])

  const setHeight = useCallback(
    (next: number) => {
      if (chartKey === undefined) return
      const clamped = clampChartHeight(next)
      setOverrides(prev => ({ ...prev, [chartKey]: clamped }))
      writeStoredChartHeight(chartKey, clamped)
    },
    [chartKey],
  )

  return { height, setHeight }
}
